/*
 * @license
 * Copyright© Celligence LLC - 2024
 */
import React, { useEffect, useRef } from "react";
import "./AnimatedGradient.css";

function AnimatedGradient() {
  const gradient1Ref = useRef(null);
  const gradient2Ref = useRef(null);
  const gradient3Ref = useRef(null);
  const gradient4Ref = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    let startTime = Date.now();

    const configs = [
      { speedX: 0.15, speedY: 0.2, rangeX: 15, rangeY: 15, offsetX: 0, offsetY: 0 },
      { speedX: -0.18, speedY: 0.22, rangeX: 15, rangeY: 15, offsetX: 60, offsetY: 5 },
      { speedX: 0.2, speedY: -0.15, rangeX: 15, rangeY: 12, offsetX: 25, offsetY: 40 },
      { speedX: 0.12, speedY: 0.18, rangeX: 15, rangeY: 15, offsetX: 70, offsetY: 50 }
    ];

    const refs = [gradient1Ref, gradient2Ref, gradient3Ref, gradient4Ref];

    const animate = () => {
      const elapsed = (Date.now() - startTime) * 0.001;

      for (let i = 0; i < refs.length; i++) {
        const el = refs[i].current;
        if (!el) continue;

        const c = configs[i];
        const moveX = c.offsetX + Math.sin(elapsed * c.speedX) * c.rangeX;
        const moveY = c.offsetY + Math.cos(elapsed * c.speedY) * c.rangeY;
        const rotation = Math.sin(elapsed * c.speedX * 0.5) * 10;
        const scale = 0.9 + Math.sin(elapsed * c.speedY * 0.4) * 0.1;
        const opacity = 0.5 + Math.sin(elapsed * c.speedX * 0.6) * 0.2;

        el.style.left = `${moveX}vw`;
        el.style.top = `${moveY}vh`;
        el.style.transform = `rotate(${rotation}deg) scale(${scale})`;
        el.style.opacity = Math.max(0.3, Math.min(0.7, opacity));
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <div className="animated-gradient-container" aria-hidden="true">
      <div ref={gradient1Ref} className="animated-gradient-blob animated-gradient-1" />
      <div ref={gradient2Ref} className="animated-gradient-blob animated-gradient-2" />
      <div ref={gradient3Ref} className="animated-gradient-blob animated-gradient-3" />
      <div ref={gradient4Ref} className="animated-gradient-blob animated-gradient-4" />
    </div>
  );
}

export default React.memo(AnimatedGradient);
